<template>
  <article
    class="list-group-item audit-log-entry audit-log-group"
    :class="{
      'audit-group-pinned': pinned,
      'audit-drop-target': dropTarget
    }"
    @contextmenu.prevent="$emit('context-menu', $event, group)"
    @dragenter.prevent="$emit('drag-over', $event, group)"
    @dragover.prevent="$emit('drag-over', $event, group)"
    @dragleave="$emit('drag-leave', $event)"
    @drop.stop="$emit('drop', group)">
    <div class="audit-log-date">
      <small v-b-tooltip.hover.html="groupDateTooltip">{{ groupDate }}</small>
    </div>

    <div class="d-flex audit-log-main-row audit-group-summary">
      <button
        type="button"
        class="audit-expand-button"
        :title="expanded ? 'Collapse group' : 'Expand group'"
        @click.stop="expanded = !expanded">
        <i :class="expanded ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"></i>
      </button>
      <span
        v-if="editable && groupsEnabled"
        class="audit-entry-checkbox audit-card-checkbox audit-checkbox-placeholder"
        aria-hidden="true">
      </span>
      <div class="audit-log-icon audit-group-icon">
        <i class="fa fa-object-group"></i>
      </div>
      <div class="pl-3">
        <img
          :src="'https://pokemonrevolution.net/forum/avatar.php?user=' + group.username"
          alt=""
          height="50"
          width="50"
          loading="lazy"
          decoding="async"
          class="rounded-circle">
      </div>
      <div class="audit-log-content pl-3">
        <div class="audit-log-meta">
          <span class="audit-staff-name" :style="{ color: groupRoleColour }">
            {{ group.username }}
          </span>
          <span
            v-for="server in groupServers"
            :key="'group-' + group.id + '-' + server"
            :class="'badge-' + getColour(server)"
            class="badge text-capitalize">
            {{ server }}
          </span>
          <span v-if="group.entries.length < group.total_member_count" class="badge badge-secondary">
            {{ group.entries.length }} / {{ group.total_member_count }} visible
          </span>
          <div
            class="audit-reason-row audit-reason-inline"
            :class="{ 'audit-reason-missing': !group.reason && !editingReason }">
            <textarea
              v-audit-resize="'reason'"
              v-if="editingReason"
              v-model="draftReason"
              class="form-control audit-reason-editor"
              rows="2"
              maxlength="2000"
              @click.stop
              @keydown.enter.prevent="saveReason"
              @keydown.esc.prevent.stop="cancelReasonEdit"
              @blur="saveReason">
            </textarea>
            <template v-else>
              <mark v-if="group.reason" class="auditReason" @click.stop>
                <template v-for="(segment, index) in reasonSegments(group.reason)">
                  <a
                    v-if="segment.url"
                    :key="'group-url-' + group.id + '-' + index"
                    href="#"
                    @click.prevent="confirmExternalLink(segment.text)">
                    <AuditLogHighlight :text="segment.text" :query="searchQuery"/>
                  </a>
                  <AuditLogHighlight
                    v-else
                    :key="'group-text-' + group.id + '-' + index"
                    :text="segment.text"
                    :query="searchQuery"/>
                </template>
              </mark>
              <button
                v-if="editable"
                type="button"
                class="btn btn-link audit-reason-edit"
                :class="group.reason ? 'text-muted' : 'text-danger'"
                :title="group.reason ? 'Edit reason' : 'Add reason'"
                @click.stop="startReasonEdit">
                <i class="fa fa-pencil"></i>
                <span v-if="!group.reason && groupIsImportant">Add required reason</span>
              </button>
            </template>
          </div>
        </div>

        <div class="audit-group-title-row">
          <input
            v-if="editable && editingTitle"
            ref="titleInput"
            v-model="draftTitle"
            class="form-control audit-group-title-input"
            maxlength="120"
            @click.stop
            @keydown.enter.prevent="$event.target.blur()"
            @keydown.esc.prevent.stop="cancelTitleEdit"
            @blur="saveTitle">
          <button
            v-else-if="editable"
            type="button"
            class="audit-group-title audit-group-title-button"
            title="Edit group title"
            @click.stop="startTitleEdit">
            <AuditLogHighlight :text="group.title" :query="searchQuery"/>
          </button>
          <strong v-else class="audit-group-title">
            <AuditLogHighlight :text="group.title" :query="searchQuery"/>
          </strong>
          <span v-if="editable" class="audit-group-actions">
            <button
              v-if="!editingTitle"
              type="button"
              class="btn btn-link text-muted audit-title-edit"
              title="Edit group title"
              @click.stop="startTitleEdit">
              <i class="fa fa-pencil"></i>
            </button>
            <button
              v-if="pinned"
              type="button"
              class="btn btn-link audit-pin-group"
              title="Unpin group"
              @click.stop="$emit('toggle-pin', group)">
              <i class="fa fa-thumb-tack"></i>
            </button>
            <button
              type="button"
              class="btn btn-link text-danger audit-delete-group"
              title="Delete group"
              @click.stop="$emit('request-delete', group)">
              <i class="fa fa-trash"></i>
            </button>
          </span>
        </div>
        <AuditLogGroupMetadata
          :fields="group.fields || []"
          :tags="group.tags || []"
          :search-query="searchQuery"
          :editable="editable">
        </AuditLogGroupMetadata>
        <AuditLogEvidence
          :items="group.evidence || []"
          :target-id="group.id"
          :editable="editableEvidence"
          target-type="group"
          @evidence-changed="$emit('evidence-changed', $event)">
        </AuditLogEvidence>
        <small class="text-muted audit-action-label">
          Group ({{ group.total_member_count || group.entries.length }} entries)
        </small>
      </div>
    </div>

    <div v-if="expanded" class="audit-group-members">
      <div
        v-for="(entry, index) in sortedEntries"
        :key="'group-entry-' + group.id + '-' + entry.id"
        class="audit-group-member"
        :class="{
          'audit-group-member-editable': editable,
          'audit-group-member-deletable': canDeleteEntry(entry)
        }">
        <span class="audit-group-number">{{ index + 1 }}.</span>
        <i
          v-b-tooltip.hover="entry.manual ? 'Self-created activity log entry' : ''"
          :class="[getIcon(entry.type), { 'audit-manual-entry-icon': entry.manual }]"
          class="audit-group-entry-icon">
        </i>
        <div class="audit-group-member-content">
          <span class="audit-group-message">
            <AuditLogHighlight :text="entry.message" :query="searchQuery"/>
          </span>
          <span class="audit-group-member-servers">
            <span
              v-for="server in sortedServers(entry.servers)"
              :key="'group-entry-' + group.id + '-' + entry.id + '-' + server"
              :class="'badge-' + getColour(server)"
              class="badge text-capitalize">
              {{ server }}
            </span>
          </span>
        </div>
        <small class="audit-group-member-date">{{ formatDateDisplay(entry.date) }}</small>
        <button
          v-if="editable"
          type="button"
          class="audit-remove-member"
          title="Remove from group"
          @click.stop="removeEntry(entry)">
          <i class="fa fa-times"></i>
        </button>
        <button
          v-if="canDeleteEntry(entry)"
          type="button"
          class="audit-delete-entry"
          title="Delete entry"
          @click.stop="$emit('request-delete-entry', entry)">
          <i class="fa fa-trash"></i>
        </button>
      </div>
    </div>
  </article>
</template>

<script>
import Permissions from '../../_permissions'
import Roles from '../../_roles'
import Servers from '../../_servers'
import { buildAuditLogActionGroups } from '../../auditlog_permissions'
import AuditLogGroupMetadata from './AuditLogGroupMetadata.vue'
import AuditLogEvidence from './AuditLogEvidence.vue'
import AuditLogHighlight from './AuditLogHighlight.vue'

const urlPattern = /(https?:\/\/[^\s<>"']+)/gi

export default {
  name: 'AuditLogGroupCard',
  inject: {
    auditLogEditorCoordinator: {
      default: null
    }
  },
  components: {
    AuditLogGroupMetadata,
    AuditLogEvidence,
    AuditLogHighlight
  },
  props: {
    group: {
      type: Object,
      required: true
    },
    editable: {
      type: Boolean,
      default: false
    },
    editableEvidence: {
      type: Boolean,
      default: false
    },
    allowDeleteEntries: {
      type: Boolean,
      default: false
    },
    allowDeleteManualEntries: {
      type: Boolean,
      default: false
    },
    searchQuery: {
      type: String,
      default: ''
    },
    groupsEnabled: {
      type: Boolean,
      default: false
    },
    pinned: {
      type: Boolean,
      default: false
    },
    dropTarget: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      permissions: Permissions,
      actionLookupGroups: buildAuditLogActionGroups(),
      expanded: false,
      editingTitle: false,
      editingReason: false,
      draftTitle: this.group.title,
      draftReason: this.group.reason || ''
    }
  },
  computed: {
    sortedEntries () {
      return (this.group.entries || []).slice().sort((left, right) => new Date(left.date) - new Date(right.date))
    },
    groupDateBounds () {
      if (this.group.oldest_entry_date && this.group.newest_entry_date) {
        return {
          oldest: this.group.oldest_entry_date,
          newest: this.group.newest_entry_date
        }
      }
      if (this.sortedEntries.length === 0) {
        return null
      }
      return {
        oldest: this.sortedEntries[0].date,
        newest: this.sortedEntries[this.sortedEntries.length - 1].date
      }
    },
    groupDate () {
      if (!this.groupDateBounds) {
        return '-'
      }
      const oldest = this.formatDateDisplay(this.groupDateBounds.oldest)
      const newest = this.formatDateDisplay(this.groupDateBounds.newest)
      return oldest === newest ? oldest : `${oldest} – ${newest}`
    },
    groupDateTooltip () {
      if (!this.groupDateBounds) {
        return 'No date data'
      }
      return this.getDateRangeTooltip(this.groupDateBounds.oldest, this.groupDateBounds.newest)
    },
    groupServers () {
      const servers = new Set()
      ;(this.group.entries || []).forEach(entry => {
        ;(entry.servers || []).forEach(server => servers.add(server))
      })
      return Array.from(servers).sort()
    },
    groupIsImportant () {
      return (this.group.entries || []).some(entry => Boolean(entry.warning))
    },
    groupRoleColour () {
      const entries = (this.group.entries || []).slice().sort((left, right) => new Date(right.date) - new Date(left.date))
      if (entries.length === 0) {
        return '#d8dee4'
      }
      const role = Roles[entries[0].staff_security_id]
      return role ? role.colour : '#d8dee4'
    }
  },
  watch: {
    group: {
      deep: true,
      handler (group) {
        if (!this.editingTitle) {
          this.draftTitle = group.title
        }
        if (!this.editingReason) {
          this.draftReason = group.reason || ''
        }
      }
    }
  },
  methods: {
    canDeleteEntry (entry) {
      return this.allowDeleteEntries || (this.allowDeleteManualEntries && Boolean(entry.manual))
    },
    sortedServers (servers) {
      return Array.isArray(servers) ? servers.slice().sort() : []
    },
    getColour (server) {
      return Servers[server] ? Servers[server].colour : server
    },
    getIcon (type) {
      if (type === 'WarnedUsers') {
        return 'fa fa-exclamation-triangle'
      }
      for (const category in this.permissions) {
        for (const permission in this.permissions[category].Permissions) {
          if (type === permission && this.permissions[category].Permissions[permission].icon) {
            return this.permissions[category].Permissions[permission].icon
          }
        }
      }
      for (const group of this.actionLookupGroups) {
        const action = group.options.find(item => item.value === type)
        if (action) {
          return action.icon
        }
      }
      return 'fa fa-pencil'
    },
    reasonSegments (reason) {
      const parts = []
      let lastIndex = 0
      reason.replace(urlPattern, (match, url, offset) => {
        if (offset > lastIndex) {
          parts.push({ text: reason.slice(lastIndex, offset), url: false })
        }
        parts.push({ text: match, url: true })
        lastIndex = offset + match.length
        return match
      })
      if (lastIndex < reason.length) {
        parts.push({ text: reason.slice(lastIndex), url: false })
      }
      return parts
    },
    async confirmExternalLink (url) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'This link opens a page outside the tools and may be unsafe.',
        {
          title: 'Open external link?',
          okTitle: 'Open Link',
          cancelTitle: 'Cancel',
          centered: true,
          headerBgVariant: 'dark',
          headerTextVariant: 'light',
          bodyBgVariant: 'dark',
          bodyTextVariant: 'light'
        }
      )
      if (confirmed) {
        window.open(url, '_blank', 'noopener')
      }
    },
    startTitleEdit () {
      if (!this.activateEditor('title', this.saveTitle)) {
        return
      }
      this.draftTitle = this.group.title
      this.editingTitle = true
      this.$nextTick(() => {
        if (this.$refs.titleInput) {
          this.$refs.titleInput.focus()
          this.$refs.titleInput.select()
        }
      })
    },
    saveTitle () {
      if (!this.editingTitle) {
        return
      }
      this.deactivateEditor('title')
      this.editingTitle = false
      const title = String(this.draftTitle || '').trim()
      if (!title || title === this.group.title) {
        this.draftTitle = this.group.title
        return
      }
      this.$set(this.group, 'title', title)
      this.emit('updateAuditLogGroup', { group_id: Number(this.group.id), title })
    },
    cancelTitleEdit () {
      if (!this.editingTitle) {
        return
      }
      this.deactivateEditor('title')
      this.editingTitle = false
      this.draftTitle = this.group.title
    },
    startReasonEdit () {
      if (!this.activateEditor('reason', this.saveReason)) {
        return
      }
      this.draftReason = this.group.reason || ''
      this.editingReason = true
    },
    saveReason () {
      if (!this.editingReason) {
        return
      }
      this.deactivateEditor('reason')
      this.editingReason = false
      const reason = String(this.draftReason || '').trim()
      if (reason === (this.group.reason || '')) {
        return
      }
      this.$set(this.group, 'reason', reason)
      this.emit('updateAuditLogGroup', { group_id: Number(this.group.id), reason })
    },
    cancelReasonEdit () {
      if (!this.editingReason) {
        return
      }
      this.deactivateEditor('reason')
      this.editingReason = false
      this.draftReason = this.group.reason || ''
    },
    removeEntry (entry) {
      const totalMemberCount = Number(this.group.total_member_count || (this.group.entries || []).length)
      const entries = (this.group.entries || []).filter(item => Number(item.id) !== Number(entry.id))
      this.$set(this.group, 'entries', entries)
      this.$set(this.group, 'total_member_count', Math.max(0, totalMemberCount - 1))
      this.emit('removeAuditLogGroupEntry', {
        group_id: Number(this.group.id),
        entry_id: Number(entry.id)
      })
    },
    editorKey (type) {
      return `pinned-group-${this.group.id}-${type}`
    },
    activateEditor (type, save) {
      if (this.auditLogEditorCoordinator) {
        return this.auditLogEditorCoordinator.activate(this.editorKey(type), save)
      }
      return true
    },
    deactivateEditor (type) {
      if (this.auditLogEditorCoordinator) {
        this.auditLogEditorCoordinator.deactivate(this.editorKey(type))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.audit-log-entry {
  position: relative;
  background: rgba(255, 255, 255, 0.025);
  border-color: rgba(255, 255, 255, 0.08);
  transition: border-color 0.12s ease, background-color 0.12s ease;
}

.audit-group-pinned {
  background: #2b3035;
  border-color: rgba(255, 255, 255, 0.16);
}

.audit-drop-target {
  border-color: #4dbd74 !important;
  background: rgba(77, 189, 116, 0.16) !important;
  box-shadow: inset 0 0 0 1px rgba(77, 189, 116, 0.45);
}

.audit-log-date {
  position: absolute;
  top: 12px;
  right: 14px;
  max-width: 45%;
  color: rgba(255, 255, 255, 0.62);
  text-align: right;
}

.audit-group-summary {
  position: relative;
  align-items: center;
  min-height: 66px;
}

.audit-expand-button,
.audit-remove-member {
  border: 0;
  background: transparent;
}

.audit-expand-button {
  position: absolute;
  z-index: 2;
  top: 50%;
  right: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  color: #d8dee4;
  transform: translateY(-50%);
}

.audit-expand-button:focus,
.audit-expand-button:active {
  outline: 0 !important;
  box-shadow: none !important;
}

.audit-log-icon {
  display: flex;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  color: #f8b84e;
  font-size: 1.25rem;
}

.audit-log-content {
  min-width: 0;
  padding-right: 210px;
}

.audit-log-meta {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
  margin-bottom: 4px;
}

.audit-staff-name,
.audit-group-title {
  font-weight: 700;
}

.auditReason,
.audit-group-message {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.auditReason {
  display: inline-block;
  margin: 0;
  padding: 2px 5px;
}

.auditReason a {
  color: #59dcc2;
  text-decoration: underline;
}

.audit-group-title-row,
.audit-reason-row {
  display: flex;
  align-items: flex-start;
  gap: 3px;
  margin-top: 7px;
}

.audit-reason-inline {
  flex: 0 1 auto;
  align-items: center;
  min-width: 0;
  margin-top: 0;
}

.audit-reason-missing {
  flex: 0 0 auto;
}

.audit-reason-edit {
  min-height: 30px;
  padding: 3px 6px;
  white-space: nowrap;
}

.audit-reason-editor {
  width: min(560px, 100%);
  min-width: 280px;
  min-height: 58px;
}

.audit-group-title-button {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
}

.audit-group-title-button:hover {
  color: #63c2de;
  text-decoration: underline;
}

.audit-group-title-input {
  max-width: 520px;
  height: 34px;
  font-weight: 700;
}

.audit-group-actions {
  display: inline-flex;
  align-items: center;
}

.audit-delete-group,
.audit-title-edit,
.audit-pin-group {
  min-height: 30px;
  padding: 3px 4px;
}

.audit-pin-group {
  color: #f8b84e;
}

.audit-action-label {
  display: block;
  margin-top: 2px;
}

.audit-entry-checkbox {
  display: flex;
  flex: 0 0 24px;
  min-width: 20px;
  align-items: center;
  justify-content: center;
  margin: 0 4px 0 0;
}

.audit-checkbox-placeholder {
  visibility: hidden;
}

.audit-group-members {
  margin: 14px 0 0 138px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.audit-group-member {
  display: grid;
  grid-template-columns: 34px 24px minmax(260px, 1fr) auto;
  gap: 8px;
  align-items: center;
  min-height: 42px;
  padding: 10px 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.audit-group-member-editable {
  grid-template-columns: 34px 24px minmax(260px, 1fr) auto 30px;
}

.audit-group-member-deletable {
  grid-template-columns: 34px 24px minmax(260px, 1fr) auto 30px;
}

.audit-group-member-editable.audit-group-member-deletable {
  grid-template-columns: 34px 24px minmax(260px, 1fr) auto 30px 30px;
}

.audit-group-number {
  color: #f39c3d;
  font-weight: 700;
  text-align: right;
}

.audit-group-entry-icon {
  color: rgba(255, 255, 255, 0.78);
  text-align: center;
}

.audit-manual-entry-icon {
  color: #20a8d8 !important;
}

.audit-delete-entry {
  border: 0;
  background: transparent;
  color: #f86c6b;
}

.audit-group-member-content {
  min-width: 0;
}

.audit-group-member-servers {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-left: 8px;
}

.audit-group-member-date {
  color: rgba(255, 255, 255, 0.58);
  white-space: nowrap;
}

.audit-remove-member {
  color: #f86c6b;
}

@media (max-width: 767px) {
  .audit-log-date {
    position: static;
    max-width: none;
    margin-bottom: 8px;
  }

  .audit-log-content {
    padding-right: 0;
  }

  .audit-log-meta {
    flex-wrap: wrap;
  }

  .audit-group-members {
    margin-left: 0;
  }

  .audit-group-member {
    grid-template-columns: 32px 22px minmax(0, 1fr);
  }

  .audit-group-member-editable {
    grid-template-columns: 32px 22px minmax(0, 1fr) 28px;
  }

  .audit-group-member-deletable {
    grid-template-columns: 32px 22px minmax(0, 1fr) 28px;
  }

  .audit-group-member-editable.audit-group-member-deletable {
    grid-template-columns: 32px 22px minmax(0, 1fr) 28px 28px;
  }

  .audit-group-member-date {
    display: none;
  }

  .audit-group-member-servers {
    display: flex;
    margin: 5px 0 0;
  }
}
</style>
